import React, { useMemo } from 'react';
import './MeanChartFat.css';

const MeanChartFat = ({ measurements, scheduleTargets }) => {
    const periods = useMemo(() => {
        if (scheduleTargets?.weightTimePeriod && scheduleTargets.weightTimePeriod.length > 0) {
            return scheduleTargets.weightTimePeriod.map(p => ({
                name: p.name,
                range: `${p.start_time} ~ ${p.end_time}`
            }));
        }
        return [
            { name: 'Morning', range: '07:00 AM ~ 11:59 AM' },
            { name: 'Afternoon', range: '12:00 PM ~ 05:59 PM' },
            { name: 'Evening', range: '06:00 PM ~ 06:59 AM' }
        ];
    }, [scheduleTargets]);

    const periodMeans = useMemo(() => {
        if (!measurements || measurements.length === 0) {
            return periods.map(p => ({ ...p, fat: null }));
        }

        return periods.map(p => {
            const periodName = p.name;
            const periodMeasurements = measurements.filter(m => {
                const mPeriod = m.period_name || m.period || 'Unknown';
                if (mPeriod.toLowerCase() === periodName.toLowerCase()) return true;

                if ((!m.period_name && !m.period) && m.measure_new_date_time) {
                    const hour = parseInt(m.measure_new_date_time.split('T')[1].substring(0, 2));
                    if (periodName === 'Morning' && hour >= 7 && hour < 12) return true;
                    if (periodName === 'Afternoon' && hour >= 12 && hour < 18) return true;
                    if (periodName === 'Evening' && (hour >= 18 || hour < 7)) return true;
                }
                return false;
            });

            if (periodMeasurements.length === 0) {
                return { ...p, fat: null };
            }

            const fatValues = periodMeasurements
                .map(m => parseFloat(m.body_fat))
                .filter(v => !isNaN(v));

            const fatMean = fatValues.length > 0
                ? fatValues.reduce((a, b) => a + b, 0) / fatValues.length
                : null;

            return { ...p, fat: fatMean };
        });
    }, [measurements, periods]);

    const yDomain = useMemo(() => {
        const validFats = periodMeans.map(p => p.fat).filter(v => v != null);
        if (validFats.length === 0) return { min: 24, max: 26, step: 0.25 };

        let rawMin = Math.min(...validFats);
        let rawMax = Math.max(...validFats);

        if (rawMin === rawMax) {
            rawMin -= 0.5;
            rawMax += 0.5;
        }

        const padding = (rawMax - rawMin) * 0.5;
        const min = Math.max(0, Math.floor(rawMin - padding));
        let max = Math.ceil(rawMax + padding);

        const range = max - min;
        let step = range > 10 ? Math.ceil(range / 5) : (range > 5 ? 2 : (range <= 2 ? 0.25 : 1));
        max = min + (step * 5);

        return { min, max, step };
    }, [periodMeans]);

    const chartWidth = 800;
    const chartHeight = 250;
    const padding = { top: 20, right: 20, bottom: 40, left: 60 };
    const plotWidth = chartWidth - padding.left - padding.right;
    const plotHeight = chartHeight - padding.top - padding.bottom;

    const scaleX = (index) => {
        if (periods.length <= 1) return padding.left + (plotWidth / 2);
        return padding.left + (index / (periods.length - 1)) * plotWidth;
    };

    const scaleY = (value) => {
        if (value == null) return null;
        return padding.top + plotHeight - ((value - yDomain.min) / (yDomain.max - yDomain.min)) * plotHeight;
    };

    const yTicks = Array.from({ length: 6 }, (_, i) => yDomain.min + (yDomain.step * i)).reverse();

    return (
        <div className="mcf-chart-section">
            <h4 className="mcf-title">Mean Chart of Fat</h4>

            <div className="mcf-chart-container">
                <div className="mcf-chart-title-center">Measurement Period Mean Chart of Fat</div>
                <div className="mcf-chart-legend">
                    <div className="mcf-legend-item">
                        <div className="mcf-legend-dot mcf-legend-fat"></div>
                        <span>Fat</span>
                    </div>
                </div>

                <div className="mcf-chart-area">
                    {periodMeans.every(p => p.fat == null) ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>No data available for the selected period</div>
                    ) : (
                        <div className="mcf-chart-placeholder">
                            <div className="mcf-y-axis">
                                {yTicks.map(tick => (
                                    <span key={tick}>{tick.toFixed(2)}</span>
                                ))}
                            </div>
                            <div className="mcf-chart-grid" style={{ width: '100%', height: '100%' }}>
                                <svg
                                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                                    preserveAspectRatio="none"
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                >
                                    {yTicks.map(value => {
                                        const y = scaleY(value);
                                        if (y == null) return null;
                                        return (
                                            <line
                                                key={`hgrid-${value}`}
                                                x1={padding.left}
                                                y1={y}
                                                x2={chartWidth - padding.right}
                                                y2={y}
                                                stroke="#ECEDEE"
                                                strokeWidth="1"
                                            />
                                        );
                                    })}

                                    {periods.map((p, index) => {
                                        const x = scaleX(index);
                                        return (
                                            <line
                                                key={`vgrid-${p.name}`}
                                                x1={x}
                                                y1={padding.top}
                                                x2={x}
                                                y2={chartHeight - padding.bottom}
                                                stroke="#e0e0e0"
                                                strokeWidth="1"
                                                strokeDasharray="2,2"
                                            />
                                        );
                                    })}

                                    {periodMeans.map((period, index) => {
                                        if (period.fat == null) return null;
                                        const x = scaleX(index);
                                        const y = scaleY(period.fat);
                                        if (y == null) return null;
                                        return (
                                            <circle
                                                key={`dot-${period.name}`}
                                                cx={x}
                                                cy={y}
                                                r="3"
                                                fill="#4B8B9B"
                                            />
                                        );
                                    })}
                                </svg>

                                <div className="mcf-x-axis">
                                    {periods.map((period, index) => (
                                        <span key={period.name} style={{ left: `${(scaleX(index) / chartWidth) * 100}%` }}>
                                            {period.range}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="mcf-y-axis-label">Blood Pressure mmHg</div> {/* Mockup literally shows this typo visually */}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MeanChartFat;
